import React, { useEffect } from 'react';
import {
  TextField,
  FormProvider,
  Flex,
  Heading,
  Text,
  Divider,
  Button,
  SelectField,
  Center,
  FiPlusCircle,
  FiTrash2,
  Box,
  DropzoneReadonly,
  FiFileText,
} from '@agrotoken-tech/ui';
import { SectionGrid } from '../../SectionGrid';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType, IDENTITY_DOCUMENT_TYPES } from '@bcba/types';
import useThirdStepForm from './hooks/useThirdStepForm';
import { FormStep } from './utils/form-step.interface';
import { S3FileUploader } from '@bcba/frontend-utils';
import { getPublicS3SignedUrl } from '../../../services/documents/getPublicS3SignedUrl';

export const ThirdStepForm = ({ setStep, stepperJSX }: FormStep) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    submitHandler,
    firstLegalRepresentativeSubTitle,
    formMethods,
    legalRepresentativesFields,
    legalRepresentativeKey,
    setValue,
    representativePositionOptions,
    shouldAddLegalRepresentativeForm,
    handleAddSecondRepresentative,
    isSubmitting,
    secondLegalRepresentativeSubTitle,
    handleRemoveSecondRepresentative,
  } = useThirdStepForm({ setStep, stepperJSX });

  const { resetField } = formMethods;

  return (
    <Flex
      w="full"
      px="48px"
      as="form"
      noValidate
      flexDir={'column'}
      onSubmit={handleSubmit(submitHandler)}
    >
      <Heading size="md" mb="12px">
        {t('businessSignUp.firstStep.title')}
      </Heading>
      {stepperJSX}
      <Text size="xl" variant="medium" mt="36px" mb="12px">
        {firstLegalRepresentativeSubTitle}
      </Text>
      <Text mb="12px">{t('businessSignUp.thirdStep.subtitle')}</Text>
      <Divider />
      <FormProvider {...formMethods}>
        {legalRepresentativesFields.map((field, index) => {
          const isSecondLegalRepresentative = index === 1;
          const legalRepresentativePrefix =
            `${legalRepresentativeKey}.${index}.` as const;

          const idDocumentFile =
            `${legalRepresentativePrefix}idDocument` as const;
          const idDocumentFileName =
            `${legalRepresentativePrefix}idDocument.name` as const;

          const signupPermissionFile =
            `${legalRepresentativePrefix}signupPermissionDocument` as const;
          const signupPermissionFileName =
            `${legalRepresentativePrefix}signupPermissionDocument.name` as const;

          // idDocumentType is fixed to DNI
          setValue(
            `${legalRepresentativePrefix}idDocumentType`,
            IDENTITY_DOCUMENT_TYPES.DNI,
          );

          const idDocumentFileNameValue = formMethods.watch(idDocumentFileName);
          const signupPermissionFileNameValue = formMethods.watch(
            signupPermissionFileName,
          );

          return (
            <React.Fragment key={field.id}>
              {isSecondLegalRepresentative ? (
                <Flex
                  justify="space-between"
                  align="center"
                  mt="36px"
                  mb="12px"
                >
                  <Text size="xl" variant="medium">
                    {secondLegalRepresentativeSubTitle}
                  </Text>
                  <Box _hover={{ cursor: 'pointer' }}>
                    <FiTrash2 onClick={handleRemoveSecondRepresentative} />
                  </Box>
                </Flex>
              ) : null}
              <SectionGrid w="full" mt="24px">
                <TextField
                  name={legalRepresentativePrefix + 'firstName'}
                  label={t(
                    'businessSignUp.form.legalRepresentativeFirstName.label',
                  )}
                  placeholder={t(
                    'businessSignUp.form.legalRepresentativeFirstName.placeholder',
                  )}
                  rhfOptions={{ required: true }}
                  errorMessageFormatter={t}
                />
                <TextField
                  name={legalRepresentativePrefix + 'lastName'}
                  label={t(
                    'businessSignUp.form.legalRepresentativeLastName.label',
                  )}
                  placeholder={t(
                    'businessSignUp.form.legalRepresentativeLastName.placeholder',
                  )}
                  rhfOptions={{ required: true }}
                  errorMessageFormatter={t}
                />

                <TextField
                  name={legalRepresentativePrefix + 'idDocumentNumber'}
                  label={t(
                    'businessSignUp.form.legalRepresentativeIdDocumentNumber.label',
                  )}
                  placeholder={t(
                    'businessSignUp.form.legalRepresentativeIdDocumentNumber.placeholder',
                  )}
                  rhfOptions={{ required: true }}
                  errorMessageFormatter={t}
                  type="number"
                />

                <TextField
                  name={legalRepresentativePrefix + 'email'}
                  label={t(
                    'businessSignUp.form.legalRepresentativeEmail.label',
                  )}
                  placeholder={t(
                    'businessSignUp.form.legalRepresentativeEmail.placeholder',
                  )}
                  rhfOptions={{ required: true }}
                  errorMessageFormatter={t}
                  type="email"
                />
                <SelectField
                  name={legalRepresentativePrefix + 'position'}
                  label={t(
                    'businessSignUp.form.legalRepresentativePosition.label',
                  )}
                  placeholder={t(
                    'businessSignUp.form.legalRepresentativePosition.placeholder',
                  )}
                  options={representativePositionOptions}
                  rhfOptions={{ required: true }}
                  errorMessageFormatter={t}
                />
              </SectionGrid>
              <Flex flexDir={'column'} gap="24px" mt="24px">
                {idDocumentFileNameValue ? (
                  <DropzoneReadonly
                    label={t('businessSignUp.form.idDocumentFile.label')}
                    fileName={idDocumentFileNameValue}
                    onClick={() => {}}
                    onDelete={() => {
                      resetField(idDocumentFile);
                    }}
                    leftIcon={FiFileText}
                  />
                ) : (
                  <S3FileUploader
                    fieldPrefix={`${idDocumentFile}.s3TempKey`}
                    label={t('businessSignUp.form.idDocumentFile.label')}
                    getS3PreSignedUrl={getPublicS3SignedUrl}
                    onFileUploadSuccess={async (
                      fileName,
                      mimeType,
                      s3TempKey,
                    ) => {
                      setValue(`${idDocumentFile}.name`, fileName);
                      setValue(
                        `${idDocumentFile}.mimeType`,
                        mimeType as FileMimeType,
                      );
                      setValue(`${idDocumentFile}.s3TempKey`, s3TempKey);
                    }}
                    onDeleteClick={() => {
                      resetField(idDocumentFile);
                    }}
                    required
                  />
                )}
                {signupPermissionFileNameValue ? (
                  <DropzoneReadonly
                    label={t('businessSignUp.form.signupPermissionFile.label')}
                    fileName={signupPermissionFileNameValue}
                    onClick={() => {}}
                    onDelete={() => {
                      resetField(signupPermissionFile);
                    }}
                    leftIcon={FiFileText}
                  />
                ) : (
                  <S3FileUploader
                    fieldPrefix={`${signupPermissionFile}.s3TempKey`}
                    label={t('businessSignUp.form.signupPermissionFile.label')}
                    getS3PreSignedUrl={getPublicS3SignedUrl}
                    onFileUploadSuccess={async (
                      fileName,
                      mimeType,
                      s3TempKey,
                    ) => {
                      setValue(`${signupPermissionFile}.name`, fileName);
                      setValue(
                        `${signupPermissionFile}.mimeType`,
                        mimeType as FileMimeType,
                      );
                      setValue(`${signupPermissionFile}.s3TempKey`, s3TempKey);
                    }}
                    onDeleteClick={() => {
                      resetField(signupPermissionFile);
                    }}
                  />
                )}
              </Flex>
            </React.Fragment>
          );
        })}

        {shouldAddLegalRepresentativeForm ? (
          <>
            <Center pt={{ sm: '3', md: '6' }} pb={{ sm: '5', md: '10' }}>
              <Button
                variant="linkPrimary"
                type="button"
                leftIcon={<FiPlusCircle />}
                onClick={handleAddSecondRepresentative}
              >
                {t(
                  'businessSignUp.form.legalRepresentativeFirstName.addSecond',
                )}
              </Button>
            </Center>
            <Divider />
          </>
        ) : null}
      </FormProvider>
      <Flex w="full" justifyContent={'flex-end'} mt="36px">
        <Button type="submit" variant={'primary'} isLoading={isSubmitting}>
          {t('businessSignUp.form.next')}
        </Button>
      </Flex>
    </Flex>
  );
};
